<template>
  <div>
    <div class="pdf-controller" v-if="controlVisible">
      <div class="pdf-controller-inner">
        <button type="button" class="pv-btn" :title="isSinglePageView ? 'Tüm sayfaları göster' : 'Tek sayfa göster'" :aria-label="isSinglePageView ? 'Tüm sayfaları göster' : 'Tek sayfa göster'" @click="toggleViewMode">
          <span v-if="isSinglePageView" class="material-symbols-outlined">file_copy</span>
          <span v-else class="material-symbols-outlined">description</span>
        </button>
        <div v-if="isSinglePageView" class="pv-group">
          <button class="pv-btn" type="button" aria-label="Önceki sayfa" @click="prevPage"
            :disabled="currentPage === 1 "><span class="material-symbols-outlined">chevron_left</span></button>
          <button class="pv-btn" type="button" aria-label="Sonraki sayfa" @click="nextPage"
            :disabled="currentPage === totalPages "><span class="material-symbols-outlined">chevron_right</span></button>
          <button class="pv-btn" type="button" aria-label="Yakınlaştır" @click="zoomIn"><span class="material-symbols-outlined">zoom_in</span></button>
          <button class="pv-btn" type="button" aria-label="Uzaklaştır" @click="zoomOut"><span class="material-symbols-outlined">zoom_out</span></button>
          <button class="pv-btn" type="button" aria-label="Sağa döndür" @click="rotate(1)"><span class="material-symbols-outlined">rotate_right</span></button>
          <button class="pv-btn" type="button" aria-label="Sola döndür" @click="rotate(2)"><span class="material-symbols-outlined">rotate_left</span></button>
        </div>
      </div>
    </div>
    <div v-if="isSinglePageView" class="pdf-controller-2">
      <div class="pdf-controller-inner-2">
        <input type="number" v-model.number="goToPage"
          @input="handlePageInput"  :min="1" :max="totalPages"> / {{ totalPages }}
      </div>
    </div>
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>Yükleniyor...</p>
    </div>
    <div
      ref="pdfContainer"
      class="pdf-touch-container"
      :style="pinchStyle"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <div v-if="isSinglePageView" :style="{ transform: rotationStyle }">
        <canvas :ref="'pdfCanvas' + currentPage" ></canvas>
        <div class="textLayer"></div>
      </div>
      <div v-else v-for="(page, index) in pages" :key="index">
        <canvas :ref="'pdfCanvas' + index"></canvas>
        <div class="textLayer"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist';
import './styles/pdf_viewer.css'

export default {
  props: {
    pdfUrl: null,
    controlVisible: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    rotationStyle() {
      return `rotate(${this.rotation}deg)`;
    },
    pinchStyle() {
      return {
        transform: `scale(${this.pinchScale})`,
        transformOrigin: `${this.pinchOriginX}% ${this.pinchOriginY}%`,
      };
    }
  },
  name: 'PdfViewer',
  data() {
    return {
      pages: [], // Array to store page references
      totalPages: 0,
      isLoading: true,
      renderingPages: [], // Track which pages are rendering
      currentPage: 1,
      isSinglePageView: false,
      zoomLevel: 1,
      fitToWidth: true, // open fitted to the screen width until the user zooms manually
      rotation: 0,
      pdf: null,
      goToPage: 1,
      devicePixelRatio: window.devicePixelRatio || 1,
      isPinching: false,
      pinchScale: 1,
      pinchOriginX: 50,
      pinchOriginY: 50,
      pinchStartDistance: 0,
      pinchStartZoom: 1,
    };
  },
  mounted() {
    this.loadPdf();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    handlePageInput() {
      if(this.goToPage > this.totalPages)
        this.goToPage = this.totalPages
      this.loadPdf(this.goToPage);
    },
    handleResize() {
      this.devicePixelRatio = window.devicePixelRatio || 1;
      this.loadPdf(this.currentPage);
    },
    async loadPdf(pageNum = 1) {
      this.isLoading = true
      try {
        // Set the worker to use for loading PDFs
        pdfjsLib.GlobalWorkerOptions.workerSrc = window.location.origin + '/js/pdf.worker.min.js';
        let loadingTask = null;
        let pdf = null;
        if (this.pdf == null) {
          // Load PDF document
          loadingTask = pdfjsLib.getDocument(this.pdfUrl);
          pdf = await loadingTask.promise;
          this.pdf = pdf
        }
        pageNum = Number(pageNum);
        if (this.fitToWidth) {
          await this.applyFitToWidthZoom(pageNum);
        }
        this.currentPage = pageNum;
        const numPages = this.pdf.numPages;
        this.totalPages = this.pdf.numPages
        this.pages = Array.from({ length: numPages }, (_, i) => i + 1); // Create an array of page numbers
        if (this.isSinglePageView) {
          await this.renderPage(this.pdf, pageNum);
          this.isLoading = false
        } else {
          for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
            await this.renderPage(this.pdf, pageNum);
            this.isLoading = false
          }
        }

      } catch {
        this.isLoading = false

      }

    },
    // Sets zoomLevel so the page width exactly fills the viewer width
    async applyFitToWidthZoom(pageNum) {
      const page = await this.pdf.getPage(pageNum);
      const pageWidth = page.getViewport({ scale: 1 }).width;
      const canvasBorder = 2; // 1px border on each side of the canvas
      const availableWidth = this.$el.clientWidth - canvasBorder;
      if (pageWidth > 0 && availableWidth > 0) {
        // canvas css width = pageWidth * zoomLevel / devicePixelRatio
        this.zoomLevel = (availableWidth * this.devicePixelRatio) / pageWidth;
      }
    },
    async renderPage(pdf, pageNum) {
      const page = await pdf.getPage(pageNum);

      let canvas = null;
      let textLayerDiv = null;
      if (this.isSinglePageView) {
        canvas = this.$refs['pdfCanvas' + pageNum];
        textLayerDiv = canvas.parentNode.querySelector('.textLayer');
      } else {
        canvas = this.$refs['pdfCanvas' + (pageNum - 1)][0];
        textLayerDiv = canvas.parentNode.querySelector('.textLayer');
      }

      const context = canvas.getContext('2d');
      
      console.log(this.zoomLevel,this.devicePixelRatio)
      const viewport = page.getViewport({ scale: this.zoomLevel });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      canvas.style.width = `${viewport.width / this.devicePixelRatio}px`;
      canvas.style.height = `${viewport.height / this.devicePixelRatio}px`;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };
      await page.render(renderContext).promise;

      textLayerDiv.innerHTML = '';
      textLayerDiv.style.width = canvas.style.width;
      textLayerDiv.style.height = canvas.style.height;
      textLayerDiv.style.left = `${canvas.offsetLeft}px`;
      textLayerDiv.style.top = `${canvas.offsetTop}px`;
      textLayerDiv.style.setProperty('--scale-factor', this.zoomLevel/this.devicePixelRatio);

      const textContent = await page.getTextContent();
      pdfjsLib.renderTextLayer({
        textContentSource: textContent,
        container: textLayerDiv,
        viewport: viewport,
        textDivs: [],
        enhanceTextSelection: true,
      });
    },
    toggleViewMode() {
      this.isSinglePageView = !this.isSinglePageView;
      if (this.isSinglePageView) {
        this.currentPage = 1
        this.renderPage(this.pdf, 1)
      }
      else
        this.loadPdf()
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.goToPage = this.currentPage;
        this.loadPdf(this.currentPage);
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.goToPage = this.currentPage;
        this.loadPdf(this.currentPage);
      }
    },
    zoomIn() {
      this.fitToWidth = false;
      this.zoomLevel += 0.5;
      this.loadPdf(this.currentPage);
    },
    zoomOut() {
      if (this.zoomLevel > 0.25) {
        this.fitToWidth = false;
        this.zoomLevel = Math.max(this.zoomLevel - 0.5, 0.25);
        this.loadPdf(this.currentPage);
      }
    },
    rotate(direction = 1) { //1 is right (clockwise) 2 is left (counter-clockwise)
      if(direction == 1)
        this.rotation = (this.rotation + 90) % 360;
      else
        this.rotation = (this.rotation - 90) % 360;

      this.renderPage(this.pdf, this.currentPage);
    },
    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },
    onTouchStart(e) {
      if (e.touches.length === 2) {
        e.preventDefault();
        this.isPinching = true;
        this.pinchStartDistance = this.getTouchDistance(e.touches);
        this.pinchStartZoom = this.zoomLevel;

        const rect = this.$refs.pdfContainer.getBoundingClientRect();
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
        this.pinchOriginX = rect.width ? (midX / rect.width) * 100 : 50;
        this.pinchOriginY = rect.height ? (midY / rect.height) * 100 : 50;
      }
    },
    onTouchMove(e) {
      if (this.isPinching && e.touches.length === 2) {
        e.preventDefault();
        const distance = this.getTouchDistance(e.touches);
        const scale = distance / this.pinchStartDistance;
        this.pinchScale = Math.min(Math.max(scale, 0.4), 2.5);
      }
    },
    async onTouchEnd(e) {
      if (this.isPinching && e.touches.length < 2) {
        this.isPinching = false;
        const newZoom = Math.min(Math.max(this.pinchStartZoom * this.pinchScale, 0.25), 8);
        this.pinchScale = 1;
        if (Math.abs(newZoom - this.zoomLevel) > 0.01) {
          this.fitToWidth = false;
          this.zoomLevel = newZoom;
          await this.loadPdf(this.currentPage);
        }
      }
    },
  }
};
</script>

<style scoped>
/* Voya: PDF görüntüleyici */
.pdf-touch-container {
  touch-action: pan-x pan-y;
}

canvas {
  border: 1px solid #E6E9EE;
  box-shadow: 0 1px 6px rgba(9, 33, 84, 0.12);
  margin-bottom: 10px;
  position: relative;
}

.pdf-controller,
.pdf-controller-2 {
  position: fixed;
  z-index: 999;
  display: flex;
  flex-direction: row;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 20px);
  right: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
}

.pdf-controller-2 {
  bottom: calc(env(safe-area-inset-bottom, 0px) + 78px);
}

.pdf-controller-inner,
.pdf-controller-inner-2 {
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  max-width: calc(100vw - 24px);
  padding: 6px;
  align-items: center;
  gap: 2px;
  border: 1px solid #EFEFEF;
  background-color: #ffffff;
  box-shadow: 0 4px 16px rgba(9, 33, 84, 0.2);
  font-family: 'Poppins', Avenir, Helvetica, Arial, sans-serif;
  font-size: 14px;
  color: #092154;
  border-radius: 100px;
  transition: all 0.2s ease-in-out;
}

.pdf-controller-inner-2 {
  padding: 6px 14px;
  gap: 8px;
  font-weight: 500;
}

.pdf-controller-inner-2 input {
  width: 56px;
  height: 32px;
  padding: 0 6px;
  border: 1px solid #E6E9EE;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  text-align: center;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  color: #092154;
}

.pdf-controller-inner-2 input:focus {
  border-color: #182F5E;
}

.pv-group {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 2px;
}

.pv-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: #092154;
  font-size: 22px;
  transition: 0.2s all ease-in-out;
}

.pv-btn .material-symbols-outlined {
  font-size: 24px;
}

.pv-btn:active {
  background-color: #EEF1F5;
}

.pv-btn:disabled {
  opacity: 0.35;
}

.textLayer {
  position: absolute;
  text-align: initial;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0.2;
  line-height: 1.0;
}

.textLayer .endOfContent {
  display: none;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(238, 240, 244, 0.85);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-family: 'Poppins', Avenir, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: #808080;
  z-index: 2;
}

.loading-overlay p {
  margin: 0;
}

.loading-spinner {
  border: 3px solid #FCE3CE;
  border-top: 3px solid #F37615;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
